import { createAsyncThunk } from "@reduxjs/toolkit";

import { ACTIVITY_STATE } from "Constants/constants";
import logger from "Libs/logger";
import { isJson } from "Libs/utils";
import { AsyncThunkOptionType } from "Reducers/types";
import { getProjectActivitiesRequest } from "Api/activity/getProjectActivities.request";

import { getActivityTypes } from "../activity.slice";

import type { Activity } from "Api/activity/types";

export interface LoadProjectPendingScheduledActivities {
  projectDescriptionId: string;
  organizationDescriptionId: string;
  filterNames: string[];
}

// Without a count, the API returns its default page (~10 items, newest
// first) *before* the state filter is meaningful protection: since any
// activity can now be scheduled (not just maintenance ones), a project
// with 10+ more-recently-created scheduled activities of unrelated types
// (crons, tasks, rescheduled redeploys) could still push the one we
// actually need off that page. This count is a generous ceiling on how
// many activities can realistically be scheduled (not yet run) at once.
const PENDING_SCHEDULED_ACTIVITIES_COUNT = 100;

export const loadProjectPendingScheduledActivities = createAsyncThunk<
  {
    activities: Activity[];
    organizationDescriptionId: string;
    projectDescriptionId: string;
  },
  LoadProjectPendingScheduledActivities,
  AsyncThunkOptionType
>(
  "app/activity/project/pendingScheduled/load",
  async (
    { projectDescriptionId, organizationDescriptionId, filterNames },
    { rejectWithValue }
  ) => {
    // Not hardcoded to maintenance types: any activity can be scheduled.
    // But it must still respect the caller's activity type filter (the
    // same one loadProjectActivities uses), or a deselected type would
    // keep showing up in Pending regardless of the filter. Matches
    // loadProjectActivities: don't request anything when no filter is
    // selected.
    const filters = getActivityTypes(filterNames);
    if (!filters.length) {
      return {
        activities: [],
        organizationDescriptionId,
        projectDescriptionId
      };
    }

    try {
      const activities = await getProjectActivitiesRequest({
        projectId: projectDescriptionId,
        filters,
        state: [ACTIVITY_STATE.SCHEDULED],
        count: PENDING_SCHEDULED_ACTIVITIES_COUNT
      });

      return {
        activities,
        organizationDescriptionId,
        projectDescriptionId
      };
    } catch (err: unknown) {
      const status = (err as { code?: number })?.code;
      if (!status || ![404, 403].includes(status)) {
        const errorMessage = isJson(err)
          ? err
          : "An error occurred while attempting to load project pending scheduled activities.";
        logger(errorMessage, {
          action: "project_load_pending_scheduled_activities",
          meta: {
            organizationDescriptionId,
            projectDescriptionId
          }
        });
      }

      return rejectWithValue(err);
    }
  }
);
